import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CollapsibleButton from './CollapsibleButton';
import * as styles from './CollapsibleContent.module.scss';

interface CollapsibleContentProps {
  maxHeight: number;
  children: (handleContentChange: () => void) => React.ReactNode;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

const CollapsibleContent = ({
  maxHeight,
  children,
  collapsed,
  onCollapsedChange,
}: CollapsibleContentProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isCollapsible, setIsCollapsible] = useState(false);
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleContentChange = useCallback(() => {
    if (mounted && ref.current) {
      const { height } = ref.current.getBoundingClientRect();
      setIsCollapsible(height > maxHeight);
    }
  }, [mounted, maxHeight]);

  useEffect(() => {
    handleContentChange();
  }, [handleContentChange]);

  const style = collapsed ? { maxHeight } : undefined;
  return (
    <div className={styles.container}>
      <div className={styles.contentContainer} style={style}>
        <div ref={ref}>{children(handleContentChange)}</div>
      </div>
      {isCollapsible && (
        <div
          className={classNames(styles.buttonBackgroundContainer, {
            [styles.collapsed]: collapsed,
          })}
        >
          <div className={styles.overlay} />
          <div className={styles.buttonContainer}>
            <CollapsibleButton
              collapsed={collapsed}
              onClick={() => onCollapsedChange(!collapsed)}
            >
              Show {collapsed ? 'more' : 'less'}
            </CollapsibleButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollapsibleContent;
